/**
 * The freshest signed URL known for each storage object.
 *
 * WHY THIS EXISTS
 * ---------------
 * The backend re-signs every image/thumbnail URL on every response with a
 * 45–60 minute TTL (backend PixelUrlLifetime). A study that is re-read while it is still arriving therefore
 * delivers a fresh signature — but nothing could consume it, because the
 * viewer deliberately PINS each instance row to the object it already holds so
 * that Cornerstone imageIds — which are identities, not just addresses — stay
 * stable. The signature baked into an imageId is whatever the first response
 * carried, so a viewer left open past the TTL could no longer fetch any slice
 * the prefetcher had not already reached: every uncached request 403s.
 *
 * The fix separates the two roles a storage URL plays:
 *
 *  - as an IDENTITY (the imageId, the Cornerstone image/dataset cache key,
 *    the annotation and metadata maps) it must never change → the viewer keeps
 *    pinning the old URL;
 *  - as an ADDRESS (the bytes actually requested over the wire) it must carry
 *    a signature that is still valid → the dispatch hook substitutes the
 *    freshest one at request time.
 *
 * This module is the handoff between them. Every delivered instance page
 * registers its URLs with {@link noteFreshUrl} before the rows are pinned (see
 * components/viewer/ViewerContext's page delivery); ./registerFallback calls
 * {@link freshestUrlFor} as it rewrites each outgoing DICOM request. Keeping
 * the imageId on the old URL is also what preserves CDN cache hits: the CDN
 * strips signing params from its cache key, so two signatures of the same
 * object are one cached entry.
 *
 * "Freshest" is qualified: a signature still in use is kept until it is close
 * to expiry (see {@link KEEP_CURRENT_URL_MIN_VALIDITY_MS}), because the
 * BROWSER's cache — unlike the CDN's — keys on the signature too.
 *
 * Deliberately dependency-free (no Cornerstone, no React) so both the query
 * layer and the loader wiring can import it — the same constraint that keeps
 * `chooseStorageLocation` out of ./registerFallback.
 *
 * Bounded by construction: {@link resetFreshUrlRegistry} is called from the
 * viewer's summary merge (hooks/use-studies) whenever the viewer switches to a
 * different study. Within one session the map holds the URLs of every study
 * the viewer has open — the current study plus any comparison priors, whose
 * pages register through the same shared delivery path — and the switch
 * clears them all together.
 */
/**
 * Signature-blind URL identity: the object path without its query params.
 * Two re-signings of the same object share an identity; two different objects
 * never do. Exported because hooks/use-studies.ts keys its merge on the same
 * notion of "is this still the same object?".
 */
export function urlObjectIdentity(url) {
    if (!url)
        return null;
    const cut = url.indexOf('?');
    return cut === -1 ? url : url.slice(0, cut);
}
/**
 * How much validity a signed URL must have left for the viewer to keep using
 * it rather than switch to a newer signature of the same object.
 *
 * WHY KEEP AN OLDER SIGNATURE AT ALL: the browser's HTTP cache is keyed on the
 * whole URL, signature and expiry included. Every switch to a newer signature
 * turns a clip the browser already holds into a cache miss and a full
 * re-download — and echo clips are tens of megabytes, re-requested whenever
 * memory pressure evicts them (lib/cornerstone/imageCacheEviction). So the
 * URL in use is kept until it is genuinely close to expiring, and only then
 * replaced. Ten minutes comfortably covers a multi-minute clip download that
 * starts just before the cut-off, plus ordinary client clock drift.
 */
export const KEEP_CURRENT_URL_MIN_VALIDITY_MS = 10 * 60_000;
const freshUrls = new Map();
/**
 * When `url`'s signature stops being accepted, in epoch ms, or null when the
 * URL carries no expiry this module knows how to read. Two shapes, both Unix
 * seconds: Cloud CDN's `Expires=` (backend CdnUrlSigner) and the LAN
 * gateway's `exp=` (backend LocalGatewayUrlSigner).
 */
export function signedUrlExpiresAtMs(url) {
    for (const pair of queryOf(url).split('&')) {
        const name = paramName(pair);
        if (name !== 'Expires' && name !== 'exp')
            continue;
        const seconds = Number(pair.slice(name.length + 1));
        return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
    }
    return null;
}
/**
 * True when `url` is signed and still has more than
 * {@link KEEP_CURRENT_URL_MIN_VALIDITY_MS} to run. An unsigned URL (or one
 * whose expiry can't be read) is never "worth keeping": for those the newest
 * URL wins, exactly as before this rule existed.
 */
function hasValidityToSpare(url) {
    const expiresAt = signedUrlExpiresAtMs(url);
    return expiresAt !== null && expiresAt - Date.now() > KEEP_CURRENT_URL_MIN_VALIDITY_MS;
}
/**
 * Record `url` as the newest signature for its object. It becomes the one in
 * use straight away unless the one in use still has validity to spare (see
 * {@link KEEP_CURRENT_URL_MIN_VALIDITY_MS}). No-op for nullish.
 */
export function noteFreshUrl(url) {
    const identity = urlObjectIdentity(url);
    if (!identity || !url)
        return;
    const entry = freshUrls.get(identity);
    if (!entry) {
        freshUrls.set(identity, { inUse: url, latest: url });
        return;
    }
    entry.latest = url;
    if (!hasValidityToSpare(entry.inUse))
        entry.inUse = url;
}
/** The URL requests for this object go out under now, switching to the
 *  latest signature once the one in use has run low. */
function urlInUse(entry) {
    if (entry.inUse !== entry.latest && !hasValidityToSpare(entry.inUse))
        entry.inUse = entry.latest;
    return entry.inUse;
}
/** Drop everything. Called when the viewer moves to a different study. */
export function resetFreshUrlRegistry() {
    freshUrls.clear();
}
/** The query string of `url` (without the `?`), or '' when it has none. */
function queryOf(url) {
    const cut = url.indexOf('?');
    return cut === -1 ? '' : url.slice(cut + 1);
}
function paramName(pair) {
    const eq = pair.indexOf('=');
    return eq === -1 ? pair : pair.slice(0, eq);
}
/**
 * Query params that are NOT signing material and therefore have to survive a
 * signature swap. Exactly one today: `createImageIdsForImage` appends
 * `&frame=N` AFTER the signed query when expanding a multi-frame instance.
 *
 * An allowlist, not "everything the fresh URL lacks" — the latter looks more
 * general but carries the STALE SIGNING PARAMS forward whenever the fresh URL
 * happens to be unsigned (a public object, or the dead `localhost:5000`
 * ingest placeholder), which is precisely backwards.
 */
const CARRIED_PARAMS = new Set(['frame']);
/**
 * The URL to actually request for `url`: `url` itself while its own signature
 * still has validity to spare (it is what earlier requests for this imageId
 * went out as, so it is what the browser has cached); otherwise the signature
 * the registry holds for the same object, with any {@link CARRIED_PARAMS} from
 * the requested URL re-appended. Returns `url` unchanged when nothing else is
 * registered for that object.
 *
 * (In @cornerstonejs/dicom-image-loader 4.22 the wadouri path never actually
 * exercises the frame carry-over: parseImageId strips `frame=N` — and the
 * separator before it — from the imageId before dataSetCacheManager hands the
 * URI to xhrRequest, so the `open` hook only ever sees the bare signed URL and
 * the loader re-applies the frame itself from the imageId. The carry-over is
 * kept because that stripping is the loader's implementation choice, not a
 * contract; the sibling lookup `cloudUrlForLocalUrl` in ./index handles a
 * `frame=` suffix for the same reason.)
 */
export function freshestUrlFor(url) {
    const identity = urlObjectIdentity(url);
    if (!identity)
        return url;
    const entry = freshUrls.get(identity);
    if (!entry)
        return url;
    if (hasValidityToSpare(url))
        return url;
    const fresh = urlInUse(entry);
    if (fresh === url)
        return url;
    const requestedQuery = queryOf(url);
    if (!requestedQuery)
        return fresh;
    const freshNames = new Set(queryOf(fresh).split('&').filter(Boolean).map(paramName));
    const carried = requestedQuery
        .split('&')
        .filter((pair) => pair && CARRIED_PARAMS.has(paramName(pair)) && !freshNames.has(paramName(pair)));
    if (carried.length === 0)
        return fresh;
    return `${fresh}${fresh.includes('?') ? '&' : '?'}${carried.join('&')}`;
}
